import { IntlShape } from 'react-intl';
import getAreaNameById from '../../../utils/getAreaNameById';
import getFleetNameById from '../../../utils/getFleetNameById';
import { CarLike } from '../types';

// The dimension cars are grouped by in the timeline. 'none' = flat (no groups).
// One at a time (single-select dropdown). All fields live on FleetAdminCarData.
export type GroupBy =
  | 'none'
  | 'area'
  | 'fleet'
  | 'status'
  | 'category'
  | 'brand'
  | 'model';

// Bucket key for cars whose grouping field is missing → rendered as "Not assigned".
// A truthy sentinel (not '') so the collapse guard `!!row.groupKey` in
// VehicleTimeline still treats the not-assigned group as collapsible.
export const NO_GROUP_KEY = '__none__';

// Drives the picker menu (order = menu order). Labels are i18n ids with fallbacks.
export const GROUP_OPTIONS: Array<{
  key: GroupBy;
  labelId: string;
  defaultLabel: string;
}> = [
  {
    key: 'none',
    labelId: 'fleetadmin.timeline.groupBy.none',
    defaultLabel: 'None',
  },
  {
    key: 'area',
    labelId: 'fleetadmin.timeline.groupBy.area',
    defaultLabel: 'Area',
  },
  {
    key: 'fleet',
    labelId: 'fleetadmin.timeline.groupBy.fleet',
    defaultLabel: 'Fleet',
  },
  {
    key: 'status',
    labelId: 'fleetadmin.timeline.groupBy.status',
    defaultLabel: 'Status',
  },
  {
    key: 'category',
    labelId: 'fleetadmin.timeline.groupBy.category',
    defaultLabel: 'Category',
  },
  {
    key: 'brand',
    labelId: 'fleetadmin.timeline.groupBy.brand',
    defaultLabel: 'Brand',
  },
  {
    key: 'model',
    labelId: 'fleetadmin.timeline.groupBy.model',
    defaultLabel: 'Model',
  },
];

// The bucket a car falls into for the active dimension. NO_GROUP_KEY when the
// field is absent (collected into the trailing "Not assigned" group).
export const getGroupKeyForCar = (car: CarLike, groupBy: GroupBy): string => {
  switch (groupBy) {
    case 'area':
      return car.areaId || NO_GROUP_KEY;
    case 'fleet':
      return car.fleetId || NO_GROUP_KEY;
    case 'status':
      return car.status || NO_GROUP_KEY;
    case 'category':
      return car.categoryId || NO_GROUP_KEY;
    case 'brand':
      return car.brand || NO_GROUP_KEY;
    case 'model':
      return car.model || NO_GROUP_KEY;
    default:
      return NO_GROUP_KEY;
  }
};

export type GroupNameCtx = {
  areas: unknown;
  fleets: unknown;
  categories: Array<{ id?: string; name?: string }> | null;
  intl: IntlShape;
  notAssignedLabel: string;
};

// Human-readable name for a bucket key. area/fleet/category resolve via lookups,
// status via the shared carStatus i18n keys, brand/model are the raw value.
export const getGroupName = (
  groupBy: GroupBy,
  key: string,
  ctx: GroupNameCtx
): string => {
  if (!key || key === NO_GROUP_KEY) return ctx.notAssignedLabel;
  switch (groupBy) {
    case 'area':
      return String(getAreaNameById(ctx.areas as any, key));
    case 'fleet':
      return String(getFleetNameById(ctx.fleets as any, key));
    case 'category': {
      const cat = Array.isArray(ctx.categories)
        ? ctx.categories.find((c) => c.id === key)
        : null;
      return cat?.name ? String(cat.name) : key;
    }
    case 'status':
      return ctx.intl.formatMessage({
        id: `fleetadmin.carStatus.${key}`,
        defaultMessage: key,
      });
    case 'brand':
    case 'model':
    default:
      return key;
  }
};
